import {
	createContext,
	type PropsWithChildren,
	useCallback,
	useContext,
} from 'react'
import { Button } from '#app/components/ui/button'
import { Icon } from '#app/components/ui/icon'
import { type IconName } from '#app/components/ui/icons/types'
import {
	type UseSupabaseUploadReturn,
	type UploadError,
	type FileWithPreview,
} from '#app/hooks/use-supabase-upload'
import { cn } from '#app/utils/misc'

export const formatBytes = (
	bytes: number,
	decimals = 2,
	size?: 'bytes' | 'KB' | 'MB' | 'GB' | 'TB' | 'PB' | 'EB' | 'ZB' | 'YB',
) => {
	const k = 1000
	const dm = decimals < 0 ? 0 : decimals
	const sizes = ['bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']

	if (bytes === 0 || bytes === undefined)
		return size !== undefined ? `0 ${size}` : '0 bytes'
	const i =
		size !== undefined
			? sizes.indexOf(size)
			: Math.floor(Math.log(bytes) / Math.log(k))
	return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i]
}

type DropzoneContextType = Omit<
	UseSupabaseUploadReturn,
	'getRootProps' | 'getInputProps'
>

const DropzoneContext = createContext<DropzoneContextType | undefined>(
	undefined,
)

type DropzoneProps = UseSupabaseUploadReturn & {
	className?: string
}

const Dropzone = ({
	className,
	children,
	getRootProps,
	getInputProps,
	...restProps
}: PropsWithChildren<DropzoneProps>) => {
	const isSuccess = restProps.isSuccess
	const isActive = restProps.isDragActive
	const isInvalid =
		(restProps.isDragActive && restProps.isDragReject) ||
		(restProps.errors.length > 0 &&
			!restProps.isSuccess &&
			restProps.errors.some(
				(error) =>
					!error.fileName ||
					restProps.files.some((file) => file.name === error.fileName),
			)) ||
		restProps.files.some((file) => file.errors.length !== 0)

	return (
		<DropzoneContext.Provider value={{ ...restProps }}>
			<div
				{...getRootProps({
					className: cn(
						'relative flex flex-col items-center overflow-hidden rounded-xl border border-dashed p-4 transition-colors',
						'data-[dragging=true]:bg-accent/50',
						'has-[input:focus]:ring-ring/50 has-[input:focus]:border-ring has-[input:focus]:ring-[3px]',
						isSuccess ? 'border-solid' : 'border-dashed',
						isActive && 'border-primary bg-primary/10',
						isInvalid && 'border-destructive bg-destructive/10',
						className,
					),
				})}
				data-dragging={isActive || undefined}
				data-files={restProps.files.length > 0 || undefined}
			>
				<input {...getInputProps()} className="sr-only" />
				{children}
			</div>
		</DropzoneContext.Provider>
	)
}

function UploadError({ error }: { error: UploadError }) {
	return (
		<div className="text-destructive text-xs">
			{error.message}
			{error.details && error.details !== error.message && (
				<span className="block opacity-80">{error.details}</span>
			)}
		</div>
	)
}

const DropzoneContent = ({ className }: { className?: string }) => {
	const {
		files,
		setFiles,
		onUpload,
		loading,
		errors,
		setErrors,
		maxFileSize,
		maxFiles,
		inputRef,
	} = useDropzoneContext()

	const handleRemoveFile = useCallback(
		(fileName: string) => {
			setFiles(files.filter((file) => file.name !== fileName))
			setErrors(errors.filter((error) => error.fileName !== fileName))
		},
		[files, setFiles, errors, setErrors],
	)

	return (
		<div className={cn('flex flex-col', className)}>
			{files.length > 0 ? (
				<div className="flex w-full flex-col gap-3">
					<div className="flex items-center justify-between gap-2">
						<h3 className="truncate text-sm font-medium">
							Files ({files.length})
						</h3>
						<div className="flex gap-2">
							<Button
								variant="success"
								size="sm"
								onClick={() => inputRef.current?.click()}
								type="button"
							>
								<Icon name="upload" />
								Add Files
							</Button>
							<Button
								variant="destructive"
								size="sm"
								onClick={() => setFiles([])}
							>
								<Icon name="trash" />
								Remove All
							</Button>
						</div>
					</div>

					<div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
						{files.map((file, idx) => {
							const fileError = errors.find((e) => e.fileName === file.name)
							const preview = getFilePreview(file)

							return (
								<div
									key={`${file.name}-${idx}`}
									className="bg-background relative flex flex-col rounded-md border"
								>
									<div className="bg-accent flex aspect-square items-center justify-center overflow-hidden rounded-t-[inherit]">
										{preview ? (
											<img
												src={preview}
												alt={file.name}
												className="size-full rounded-t-[inherit] object-cover"
											/>
										) : (
											<Icon
												name={getFileIcon(file) as IconName}
												className="size-5 opacity-60"
											/>
										)}
									</div>

									{!loading && (
										<Button
											onClick={() => handleRemoveFile(file.name)}
											size="icon"
											className="border-background focus-visible:border-background absolute -top-2 -right-2 size-6 rounded-full border-2 shadow-none"
										>
											<Icon name="cross-1" className="size-3.5" />
										</Button>
									)}

									<div className="flex min-w-0 flex-col gap-0.5 border-t p-3">
										<p className="truncate text-[13px] font-medium">
											{file.name}
										</p>
										{file.errors.length > 0 ? (
											<UploadError
												error={{
													message: file.errors[0]?.message ?? 'Invalid file',
													code: file.errors[0]?.code ?? 'invalid_file',
												}}
											/>
										) : loading ? (
											<p className="text-muted-foreground text-xs">
												Uploading file...
											</p>
										) : fileError ? (
											<UploadError error={fileError} />
										) : (
											<p className="text-muted-foreground text-xs">
												{formatBytes(file.size, 2)}
											</p>
										)}
									</div>
								</div>
							)
						})}
					</div>

					{files.length > 0 && files.length <= maxFiles && (
						<div className="mt-2">
							<Button
								className="w-full"
								size="lg"
								onClick={onUpload}
								disabled={
									files.some((file) => file.errors.length !== 0) || loading
								}
							>
								{loading ? (
									<>
										<Icon name="update" className="animate-spin" />
										Uploading...
									</>
								) : (
									<>
										<Icon name="upload" />
										Upload Files
									</>
								)}
							</Button>
						</div>
					)}
				</div>
			) : null}

			{errors
				.filter((error) => !error.fileName)
				.map((error, index) => (
					<div key={index} className="mb-4">
						<div className="flex items-center gap-1">
							<Icon name="exclamation-triangle" className="size-3 shrink-0" />
							<UploadError error={error} />
						</div>
					</div>
				))}
		</div>
	)
}

const DropzoneEmptyState = ({ className }: { className?: string }) => {
	const { maxFiles, maxFileSize, inputRef, files } = useDropzoneContext()

	if (files.length > 0) return null

	return (
		<div
			className={cn(
				'flex flex-col items-center justify-center px-4 py-3 text-center',
				className,
			)}
		>
			<div
				className="bg-background mb-2 flex size-11 shrink-0 items-center justify-center rounded-full border"
				aria-hidden="true"
			>
				<Icon name="image" className="size-4 opacity-60" />
			</div>
			<p className="mb-1.5 text-sm font-medium">Drop your files here</p>
			<p className="text-muted-foreground text-xs">
				Max {maxFiles} file{maxFiles !== 1 ? 's' : ''} · Up to{' '}
				{formatBytes(maxFileSize, 0)}
			</p>
			<Button
				variant="outline"
				className="mt-4"
				onClick={() => inputRef.current?.click()}
			>
				<Icon name="hand" className="-ms-1 opacity-60" />
				Select Files
			</Button>
		</div>
	)
}

const useDropzoneContext = () => {
	const context = useContext(DropzoneContext)

	if (!context) {
		throw new Error('useDropzoneContext must be used within a Dropzone')
	}

	return context
}

// File type utilities
const getFileIcon = (file: FileWithPreview): IconName => {
	const fileType = file.type
	const fileName = file.name

	if (fileType.startsWith('image/')) return 'image'
	if (fileType.includes('pdf') || fileName.endsWith('.pdf')) return 'file-text'
	if (fileType.includes('zip') || fileName.endsWith('.zip')) return 'file'
	if (fileType.includes('excel') || fileName.endsWith('.xlsx')) return 'file'
	if (fileType.includes('video/')) return 'file'
	if (fileType.includes('audio/')) return 'file'

	return 'file'
}

const getFilePreview = (file: FileWithPreview): string | null => {
	if (file.type.startsWith('image/')) {
		return URL.createObjectURL(file)
	}
	return null
}

export { Dropzone, DropzoneContent, DropzoneEmptyState, useDropzoneContext }
